<template>
  <div class="domain-ranking-list" :class="{ 'no-data': noData }">
    <button class="more-button" @click="onMoreButtonClick">
      <span>{{ $t('dashboard.analysis.ranking.morePages') }}</span>
      <i class="icon icon--arrow-right-red size-13"></i>
    </button>
    <div class="table-wrap">
      <table class="table">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.key"
              :class="column.key"
              :style="{ width: column.width }"
            >
              <span>
                {{ $t('ranking.column.' + column.key) }}
                <div
                  class="url-switch"
                  v-if="column.key === 'pageData'"
                >
                  <span @click="isUrlSwitched = false">URL</span>
                  <ba-switch
                    :value="isUrlSwitched"
                    :default="false"
                    :onIcon="'icon--arrow-body-dark-gray size-10'"
                    :offIcon="noData ? 'icon--arrow-small-right-gray size-10' : 'icon--arrow-body-dark-gray size-10'"
                    :offColor="noData ? '#D7D7D7' : '#3eaa08'"
                    :onLabel="''"
                    :offLabel="''"
                    :width="30"
                    :size="'xsmall'"
                    @change="isUrlSwitched = !isUrlSwitched"
                  />
                  <span @click="isUrlSwitched = true">Title</span>
                </div>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in listDataArr" :key="index">
            <td
              v-for="column in columns"
              :key="column.key"
              :class="[column.key, column.type]"
              :style="{ width: column.width }"
              :title="column.title ? item[column.title] : ''"
            >
              <template v-if="column.type === 'index'">{{ item.rank }}</template>
              <template v-else-if="column.type === 'url'">
                <template v-if="isUrlSwitched">{{ item.title }}</template>
                <template v-else>{{ item.listUrl }}</template>
              </template>
              <template v-else-if="column.type === 'number'">{{ item[column.key] | letterThousandMillion(0) }}</template>
              <template v-else-if="column.type === 'ratio'">{{ getPercentage(item[column.key], item.count) | fixed(column.decimals) }}%</template>
              <template v-else-if="column.type === 'time'">{{ getTimeString(item[column.key])}}</template>
              <template v-else>{{ item[column.key] }}</template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="!noData" class="page-navigation">
      <ui-navigation
        :currentPage="page"
        :lastPage="lastPage"
        @setPage="setPage"
      />
    </div>
    <p class="no-data" v-else>
      {{ $t("dashboard.analysis.noData") }}
    </p>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import { getTimeString, getPercentage } from '@/libs/utils'
import UiNavigation from '@/components/ui/UiNavigation'
import BaSwitch from '@/components/ui/ba/Switch'

export default {
  name: 'DomainRankingList',
  components: {
    BaSwitch,
    UiNavigation
  },
  props: {
    listData: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      isUrlSwitched: false,
      page: 1,
      limit: 5,
      columns: [
        {
          key: 'rank',
          width: '7.12%',
          type: 'index'
        },
        {
          key: 'pageData',
          width: '33.72%',
          type: 'url',
          title: 'listUrl'
        },
        {
          key: 'count',
          width: '9.79%',
          type: 'number'
        },
        {
          key: 'stayInCount',
          width: '9.79%',
          type: 'ratio',
          decimals: 2
        },
        {
          key: 'dropOff',
          width: '9.79%',
          type: 'ratio',
          decimals: 2
        },
        {
          key: 'rollback',
          width: '9.79%',
          type: 'ratio',
          decimals: 1
        },
        {
          key: 'reloadCount',
          width: '10.23%',
          type: 'ratio',
          decimals: 1
        },
        {
          key: 'avgStay',
          width: '9.79%',
          type: 'time'
        }
      ]
    }
  },
  computed: {
    ...mapState([
      'domain',
      'userData',
      'journeyOptions',
      'journeyPeriod'
    ]),
    rankedListData () {
      if (!this.listData) return []

      let currentRank = 1
      return this.listData.map((item, index) => {
        if (index > 0 && item.count !== this.listData[index - 1].count) {
          currentRank = index + 1
        }
        return {
          ...item,
          rank: currentRank
        }
      })
    },
    listDataArr () {
      const startIndex = (this.page - 1) * this.limit
      const endIndex = startIndex + this.limit
      return this.rankedListData.slice(startIndex, endIndex)
    },
    lastPage () {
      return this.listData ? Math.ceil(this.listData.length / this.limit) : 0
    },
    noData () {
      return !this.listData.length
    }
  },
  methods: {
    getPercentage (value, total) {
      return getPercentage(value, total)
    },
    setPage (page) {
      this.page = page
    },
    getTimeString (value) {
      return getTimeString(value / 1000)
    },
    onMoreButtonClick () {
      const queryData = {
        sid: this.domain.orgSid || this.domain.sid
      }

      if (this.domain.shared === 'shared') {
        queryData.subSid = this.domain.subDomains[0].orgSid
        queryData.path = null
      }

      if (this.userData.userId === 'samsung_gmc') {
        queryData.subSid = this.domain.subDomains.find(subDomain => subDomain.domain === 'samsung.com').orgSid
        queryData.path = 'uk'
      }

      let routeData = this.$router.resolve({
        name: 'ranking',
        query: queryData
      })

      const newWindow = window.open(routeData.href, '_blank')
      newWindow._journeyOptionsFromParent = { ...this.journeyOptions }
      newWindow._journeyPeriodFromParent = { ...this.journeyPeriod }
    }
  }
}
</script>
<style lang="scss" scoped>
.domain-ranking-list {
  position: relative;
  .more-button {
    position: absolute;
    top: -10px;
    right: -7px;
    transform: translateY(-100%);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    padding: 10px;
    color: #444;
    font-size: 13px;
    font-weight: 600;
    &:hover {
      color: #777;
    }
  }
  .table-wrap {
    min-height: 246px;
    .table {
      width: 100%;
      thead {
        color: #777;
        font-weight: 500;
        tr {
          height: 40px;
          border-bottom: 1px solid #bbb;
          th {
            padding: 6px 12px;
            vertical-align: bottom;
            line-height: 1.2;
            &.pageData {
              span {
                position: relative;
                .url-switch {
                  position: absolute;
                  right: -25px;
                  top: 50%;
                  transform: translate(100%, -50%);
                  display: flex;
                  align-items: center;
                  gap: 9px;
                  cursor: pointer;
                  /deep/ .ba-switch {
                    button .pin i.off {
                      transform: translate(-50%, -50%) scaleX(-1);
                    }
                  }
                  &:before {
                    content: '';
                    position: absolute;
                    left: -12px;
                    top: 50%;
                    transform: translateY(-50%);
                    width: 1px;
                    height: 8px;
                    background-color: #D9D9D9;
                  }
                }
              }
            }
          }
        }
      }
      tbody {
        color: #444;
        font-weight: 400;
        tr {
          height: 41px;
          border-bottom: 1px solid #ebebeb;
          td {
            @include textEllipsis;
            line-height: 1.5;
          }
        }
      }
      tr > th,
      tr > td {
        max-width: 0;
        padding: 6px 12px;
        font-size: 13px;
        box-sizing: border-box;
        text-align: start;
        &.rank {
          text-align: center;
        }
        &.count {
          background-color: #f4f4f4;
          font-weight: 600;
          color: #2f2f2f;
        }
      }
    }
  }
  .page-navigation {
    @include flexXYcenter;
    margin-top: 24px;
    .ui-navigation {
      padding-top: 0;
    }
  }
  .no-data {
    position: absolute;
    top: 165px;
    left: 0;
    width: 100%;
    color: #999;
    font-size: 12px;
    font-weight: 500;
    text-align: center;
  }
  &.no-data {
    pointer-events: none;
    .more-button {
      opacity: 0.3;
    }
    .table-wrap .table {
      tr > th.count {
        background-color: inherit;
        font-weight: inherit;
        color: inherit;
      }
    }
  }
}
</style>
